import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  MENSAJE_COLEGIADO, MENSAJE_FORMATO, MENSAJE_FORMATO_PERSONA, TITULOS, componerNombreMedico,
  esTipoMedico, normalizarColegiado, normalizarNombreMedico, normalizarNombrePersona,
} from '../../lib/medicos';

/**
 * AltaProfesionalDialog — alta de un cirujano o anestesista nuevo desde el
 * registro de quirófano (2026-09-08, punto 7).
 *
 * Un profesional que no está en el desplegable se da de alta aquí, con el
 * mismo formato obligatorio que el resto (Dr./Dra. Nombre y dos apellidos,
 * más su número de colegiado; los dos campos obligatorios) y pasa solo a
 * formar parte del desplegable correspondiente.
 *
 * Los campos van por separado (tratamiento, nombre, primer y segundo
 * apellido, colegiado) para que el formato salga solo; el backend
 * (POST /api/qx-catalogo) lo vuelve a validar y devuelve la entrada, que el
 * padre selecciona en el acto.
 *
 * Instrumentistas y circulantes (2026-09-09, punto 7): el mismo
 * formulario, pero SIN tratamiento y SIN colegiado, porque «no son medicos»:
 * «Nombre y dos Apellidos». Así se acabaron las entradas genéricas del
 * desplegable («Enfermería», «Otro», «Externo», un nombre de pila a secas).
 *
 * Props: { open, onOpenChange, tipo: 'cirujano'|'anestesista'|'instrumentista'|'circulante',
 *          textoInicial?: lo tecleado en el buscador («Dra. Nombre Apellido»),
 *          clinica?: clínica de origen que se adjunta (solo cirujanos),
 *          onCreated(entry) }
 */
const TITULO_DIALOGO = {
  cirujano: 'Dar de alta un cirujano',
  anestesista: 'Dar de alta un anestesista',
  instrumentista: 'Dar de alta un instrumentista',
  circulante: 'Dar de alta un circulante',
};
const VACIO = { titulo: 'Dr.', nombre: '', apellido1: '', apellido2: '', colegiado: '', externo: false };

/** Reparte «Dra. Nombre Apellido1 Apellido2» en los campos del formulario. */
function desdeTexto(texto) {
  const partes = (texto || '').trim().split(/\s+/).filter(Boolean);
  const f = { ...VACIO };
  if (!partes.length) return f;
  const primera = partes[0].replace(/\.$/, '').toLowerCase();
  if (primera === 'dr' || primera === 'dra') {
    f.titulo = primera === 'dra' ? 'Dra.' : 'Dr.';
    partes.shift();
  }
  f.nombre = partes[0] || '';
  f.apellido1 = partes[1] || '';
  f.apellido2 = partes.slice(2).join(' ');
  return f;
}

export default function AltaProfesionalDialog({ open, onOpenChange, tipo, textoInicial, clinica, onCreated }) {
  const [form, setForm] = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const esCirujano = tipo === 'cirujano';
  // Instrumentistas y circulantes no son médicos: ni tratamiento ni colegiado.
  const esMedico = esTipoMedico(tipo);
  const rol = tipo || 'profesional';

  useEffect(() => {
    if (open) {
      setForm(desdeTexto(textoInicial));
      setSaving(false);
    }
  }, [open, textoInicial]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mensajeFormato = esMedico ? MENSAJE_FORMATO : MENSAJE_FORMATO_PERSONA;
  const nombreCompleto = componerNombreMedico({ ...form, titulo: esMedico ? form.titulo : '' });
  const nombreOk = esMedico ? normalizarNombreMedico(nombreCompleto) : normalizarNombrePersona(nombreCompleto);
  const colegiadoOk = normalizarColegiado(form.colegiado);

  const submit = async (e) => {
    e.preventDefault();
    if (!nombreOk) {
      toast.error(mensajeFormato);
      return;
    }
    if (esMedico && !colegiadoOk) {
      toast.error(MENSAJE_COLEGIADO);
      return;
    }
    setSaving(true);
    try {
      const payload = esMedico ? { tipo, nombre: nombreOk, colegiado: colegiadoOk } : { tipo, nombre: nombreOk };
      if (esCirujano && clinica) payload.clinica = clinica;
      // Cirujano EXTERNO (2026-09-08, punto 6): factura el uso del
      // quirófano y sale en el Registro de externos. Por defecto, colaborador.
      if (esCirujano && form.externo) payload.externo = true;
      const r = await api.post('/api/qx-catalogo', payload);
      toast.success(`${r.data?.nombre || nombreOk} dado de alta: ya sale en el desplegable de ${rol}s`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo dar de alta'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="alta-profesional-dialog">
        <DialogHeader>
          <DialogTitle>{TITULO_DIALOGO[tipo] || 'Dar de alta un profesional'}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          {esMedico ? (
            <>
              Formato obligatorio: <strong>Dr./Dra. Nombre Apellido1 Apellido2</strong>, con tildes y mayúsculas,
              y su número de colegiado. Quedará en el desplegable de {rol}s para la próxima vez.
            </>
          ) : (
            <>
              Formato obligatorio: <strong>Nombre Apellido1 Apellido2</strong>, con tildes y mayúsculas.
              No llevan Dr./Dra. ni número de colegiado (no son médicos). Quedará en el desplegable
              de {rol}s para la próxima vez.
            </>
          )}
        </p>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {esMedico && (
          <div>
            <Label>Tratamiento</Label>
            <Select value={form.titulo} onValueChange={(v) => setF('titulo', v)}>
              <SelectTrigger data-testid="alta-titulo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TITULOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          )}
          <div>
            <Label>Nombre <span className="text-red-600">*</span></Label>
            <Input value={form.nombre} onChange={(e) => setF('nombre', e.target.value)} placeholder="Nombre" data-testid="alta-nombre" autoFocus />
          </div>
          <div>
            <Label>Primer apellido <span className="text-red-600">*</span></Label>
            <Input value={form.apellido1} onChange={(e) => setF('apellido1', e.target.value)} placeholder={esMedico ? 'García' : 'Pérez'} data-testid="alta-apellido1" />
          </div>
          <div>
            <Label>Segundo apellido <span className="text-red-600">*</span></Label>
            <Input value={form.apellido2} onChange={(e) => setF('apellido2', e.target.value)} placeholder={esMedico ? 'González' : 'López'} data-testid="alta-apellido2" />
          </div>
          {esMedico && (
          <div className="sm:col-span-2">
            <Label>Nº de colegiado <span className="text-red-600">*</span></Label>
            <Input
              inputMode="numeric"
              value={form.colegiado}
              onChange={(e) => setF('colegiado', e.target.value)}
              placeholder="000000000"
              data-testid="alta-colegiado"
            />
          </div>
          )}
          {esCirujano && (
            <label className="sm:col-span-2 flex items-start gap-2 text-xs cursor-pointer" htmlFor="alta-externo">
              <Checkbox
                id="alta-externo"
                checked={!!form.externo}
                onCheckedChange={(v) => setF('externo', !!v)}
                data-testid="alta-externo"
                className="mt-0.5"
              />
              <span>
                <strong>Cirujano externo</strong>: factura el uso del quirófano y sus intervenciones salen en el
                Registro de externos. Sin marcar es colaborador de la clínica.
              </span>
            </label>
          )}
          <div className="sm:col-span-2 text-xs rounded bg-[rgba(46,110,94,0.08)] px-3 py-2" data-testid="alta-vista-previa">
            {nombreOk
              ? (
                <>
                  Se guardará como <strong>{nombreOk}</strong>
                  {!esMedico ? null : colegiadoOk
                    ? <> · Colegiado <strong>{colegiadoOk}</strong></>
                    : <span className="text-amber-700"> · falta el colegiado</span>}
                </>
              )
              : <span className="text-amber-700">{mensajeFormato}</span>}
          </div>
          <div className="sm:col-span-2">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="alta-guardar">
                {saving ? 'Guardando…' : 'Dar de alta'}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
